import React, { useState, useEffect, useRef, useCallback } from 'react';
import { inmuebleService } from '../services/inmuebleService';
import { ingresosGastosService } from '../services/ingresosGastosService';
import { Inmueble } from '../types/Inmueble';
import { IngresosGastos, CATEGORIA_ORDER, CATEGORIAS_INGRESO, CATEGORIAS_GASTO } from '../types/IngresosGastos';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from '../components/common/EmptyState';
import { BsBuilding } from 'react-icons/bs';
import './IngresosGastos.css';

const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function formatCell(v: number): string {
  const abs = Math.abs(v);
  const formatted = new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(abs);
  return v < 0 ? `(${formatted})` : formatted;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

interface TableRow {
  concepto: string;
  meses: (IngresosGastos | null)[];
  multiMeses: boolean[];
  total: number;
}

interface EntryForm {
  concepto: string;
  categoria: string;
  dia: number;
  mes: number;
  anio: number;
  cantidad: string;
}

function buildTableData(records: IngresosGastos[]): Map<string, TableRow[]> {
  const grouped = new Map<string, Map<string, Map<number, IngresosGastos[]>>>();

  for (const r of records) {
    const mes = new Date(r.fecha).getMonth() + 1;
    if (!grouped.has(r.categoria)) grouped.set(r.categoria, new Map());
    const byConcepto = grouped.get(r.categoria)!;
    if (!byConcepto.has(r.concepto)) byConcepto.set(r.concepto, new Map());
    const byMes = byConcepto.get(r.concepto)!;
    if (!byMes.has(mes)) byMes.set(mes, []);
    byMes.get(mes)!.push(r);
  }

  const result = new Map<string, TableRow[]>();

  for (const cat of CATEGORIA_ORDER) {
    const byConcepto = grouped.get(cat);
    if (!byConcepto) continue;

    const rows: TableRow[] = [];
    for (const [concepto, byMes] of byConcepto) {
      const meses: (IngresosGastos | null)[] = Array(12).fill(null);
      const multiMeses: boolean[] = Array(12).fill(false);
      let total = 0;

      for (let m = 1; m <= 12; m++) {
        const recs = byMes.get(m) ?? [];
        if (recs.length === 0) {
          meses[m - 1] = null;
        } else if (recs.length === 1) {
          meses[m - 1] = recs[0];
          total += Number(recs[0].cantidad);
        } else {
          const sum = recs.reduce((acc, r) => acc + Number(r.cantidad), 0);
          meses[m - 1] = { ...recs[0], cantidad: sum };
          multiMeses[m - 1] = true;
          total += sum;
        }
      }

      rows.push({ concepto, meses, multiMeses, total });
    }

    if (rows.length > 0) result.set(cat, rows);
  }

  return result;
}

const emptyForm = (year: number): EntryForm => ({
  concepto: '',
  categoria: 'Ingresos',
  dia: new Date().getDate(),
  mes: new Date().getMonth() + 1,
  anio: year,
  cantidad: '',
});

interface IngresosGastosProps {
  onRegisterAction?: (info: { action: () => void; label: string; visible: boolean }) => void;
}

const IngresosGastosPage: React.FC<IngresosGastosProps> = ({ onRegisterAction }) => {
  const [inmuebles, setInmuebles] = useState<Inmueble[]>([]);
  const [selectedInmueble, setSelectedInmueble] = useState<string>('');
  const [year, setYear] = useState<number>(new Date().getFullYear());
  const [records, setRecords] = useState<IngresosGastos[]>([]);
  const [loading, setLoading] = useState(false);

  // Modal unificado (add + edit)
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState<IngresosGastos | null>(null);
  const [form, setForm] = useState<EntryForm>(emptyForm(new Date().getFullYear()));
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Autocomplete concepto
  const [knownConcepts, setKnownConcepts] = useState<string[]>([]);
  const [conceptoSuggestions, setConceptoSuggestions] = useState<string[]>([]);
  const conceptoRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inmuebleService.getAll().then((res) => {
      setInmuebles(res.data);
      if (res.data.length > 0) setSelectedInmueble(res.data[0].id!);
    });
  }, []);

  useEffect(() => {
    setKnownConcepts([]);
  }, [selectedInmueble]);

  const loadRecords = useCallback(async () => {
    if (!selectedInmueble) return;
    setLoading(true);
    try {
      const res = await ingresosGastosService.getAll(selectedInmueble, year);
      setRecords(res.data);
      setKnownConcepts((prev) => {
        const merged = new Set([...prev, ...res.data.map((r) => r.concepto)]);
        return [...merged].sort();
      });
    } catch (e) {
      console.error('Error cargando registros:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedInmueble, year]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const openAddModal = () => {
    setEditingRecord(null);
    setForm(emptyForm(year));
    setFormError('');
    setConceptoSuggestions([]);
    setShowModal(true);
  };

  useEffect(() => {
    onRegisterAction?.({ action: openAddModal, label: 'Crear Entrada', visible: !!selectedInmueble });
  }, [selectedInmueble, onRegisterAction]);

  const openEditModal = (record: IngresosGastos, categoria: string, concepto: string, mes: number) => {
    const [anioStr, , diaStr] = record.fecha.split('-');
    setEditingRecord(record);
    setForm({
      concepto,
      categoria,
      dia: parseInt(diaStr, 10),
      mes,
      anio: parseInt(anioStr, 10),
      cantidad: String(record.cantidad),
    });
    setFormError('');
    setConceptoSuggestions([]);
    setShowModal(true);
  };

  const openNewCellModal = (categoria: string, concepto: string, mes: number) => {
    setEditingRecord(null);
    setForm({
      concepto,
      categoria,
      dia: 1,
      mes,
      anio: year,
      cantidad: '',
    });
    setFormError('');
    setConceptoSuggestions([]);
    setShowModal(true);
  };

  const handleCellClick = (
    categoria: string,
    concepto: string,
    mes: number,
    existing: IngresosGastos | null,
    isMulti: boolean
  ) => {
    if (isMulti) return;
    if (existing) {
      openEditModal(existing, categoria, concepto, mes);
    } else {
      openNewCellModal(categoria, concepto, mes);
    }
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    const cantidad = parseFloat(form.cantidad.replace(',', '.'));
    if (isNaN(cantidad) || cantidad === 0) {
      setFormError('La cantidad no puede ser 0 ni vacía.');
      return;
    }
    const isIngreso = (CATEGORIAS_INGRESO as readonly string[]).includes(form.categoria);
    const isGasto = (CATEGORIAS_GASTO as readonly string[]).includes(form.categoria);
    if (isIngreso && cantidad < 0) {
      setFormError('La categoría "Ingresos" solo admite cantidades positivas.');
      return;
    }
    if (isGasto && cantidad > 0) {
      setFormError('Las categorías de gasto solo admiten cantidades negativas.');
      return;
    }
    const fecha = `${form.anio}-${pad2(form.mes)}-${pad2(form.dia)}`;
    setSaving(true);
    try {
      if (editingRecord?.id) {
        await ingresosGastosService.update(editingRecord.id, {
          ...editingRecord,
          idInmueble: selectedInmueble,
          fecha,
          cantidad,
          concepto: form.concepto.trim(),
          categoria: form.categoria,
        });
      } else {
        await ingresosGastosService.create({
          idInmueble: selectedInmueble,
          fecha,
          cantidad,
          concepto: form.concepto.trim(),
          categoria: form.categoria,
        });
      }
      setShowModal(false);
      await loadRecords();
    } catch (e) {
      console.error('Error guardando:', e);
      setFormError('Error al guardar. Inténtalo de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingRecord?.id) return;
    setDeleting(true);
    try {
      await ingresosGastosService.delete(editingRecord.id);
      setShowModal(false);
      await loadRecords();
    } catch (e) {
      console.error('Error eliminando:', e);
      setFormError('Error al eliminar. Inténtalo de nuevo.');
    } finally {
      setDeleting(false);
    }
  };

  const tableData = buildTableData(records);

  // Totales por mes y gran total
  const monthTotals = Array(12).fill(0) as number[];
  let grandTotal = 0;
  for (const rows of tableData.values()) {
    for (const row of rows) {
      row.meses.forEach((cell, i) => {
        if (cell) monthTotals[i] += Number(cell.cantidad);
      });
      grandTotal += row.total;
    }
  }

  // Totales por categoría para filas derivadas
  const catMonths = new Map<string, number[]>();
  for (const [cat, rows] of tableData.entries()) {
    const m = Array(12).fill(0) as number[];
    for (const row of rows) {
      row.meses.forEach((cell, i) => { if (cell) m[i] += Number(cell.cantidad); });
    }
    catMonths.set(cat, m);
  }
  const get12 = (key: string) => catMonths.get(key) ?? (Array(12).fill(0) as number[]);
  const ingresosM = get12('Ingresos');
  const gastosOrdM = get12('Gastos ordinarios');
  const gastosDirectM = get12('Gastos directos');
  const gastosReservaM = get12('Gastos reserva');
  const gastosReinversionM = get12('Gastos reinversión');
  const resultadoMonths = ingresosM.map((v, i) => v + gastosOrdM[i] + gastosDirectM[i]);
  const reservaMonths = resultadoMonths.map(v => v * 0.1);
  const reinversionMonths = resultadoMonths.map(v => v * 0.2);
  const inversoresMonths = resultadoMonths.map(v => v * 0.7);
  const reservaNetaMonths = reservaMonths.map((v, i) => v + gastosReservaM[i]);
  const reinversionNetaMonths = reinversionMonths.map((v, i) => v + gastosReinversionM[i]);
  const resultado = resultadoMonths.reduce((a, b) => a + b, 0);
  const reservaTotal = resultado * 0.1;
  const reinversionTotal = resultado * 0.2;
  const inversoresTotal = resultado * 0.7;
  const reservaNetaTotal = reservaNetaMonths.reduce((a, b) => a + b, 0);
  const reinversionNetaTotal = reinversionNetaMonths.reduce((a, b) => a + b, 0);

  return (
    <div className="ig-page">

      <div className="ig-controls">
        <div className="ig-control-group">
          <label htmlFor="ig-inmueble-select">Inmueble:</label>
          <select
            id="ig-inmueble-select"
            value={selectedInmueble}
            onChange={(e) => setSelectedInmueble(e.target.value)}
            className="ig-select"
          >
            {inmuebles.length === 0 && <option value="">Sin inmuebles</option>}
            {inmuebles.map((i) => (
              <option key={i.id} value={i.id}>
                {i.direccion || i.idProyecto || i.id}
              </option>
            ))}
          </select>
        </div>

        <div className="ig-control-group ig-year-nav">
          <button className="ig-year-btn" onClick={() => setYear((y) => y - 1)}>&#8249;</button>
          <span className="ig-year-label">{year}</span>
          <button className="ig-year-btn" onClick={() => setYear((y) => y + 1)}>&#8250;</button>
        </div>
      </div>

      {!selectedInmueble ? (
        <EmptyState icon={BsBuilding} title="Selecciona un inmueble" subtitle="Elige un inmueble del desplegable para ver sus registros." />
      ) : loading ? (
        <LoadingSpinner />
      ) : (
        <div className="ig-table-wrapper">
          <table className="ig-table">
            <thead>
              <tr>
                <th className="ig-col-concepto">Concepto</th>
                {MESES.map((m) => (
                  <th key={m} className="ig-col-mes">{m}</th>
                ))}
                <th className="ig-col-total">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIA_ORDER.map((cat) => {
                const rows = tableData.get(cat);
                if (!rows || rows.length === 0) return null;
                return (
                  <React.Fragment key={cat}>
                    <tr className="ig-category-header">
                      <td colSpan={14}>{cat}</td>
                    </tr>
                    {rows.map((row) => (
                      <tr key={row.concepto}>
                        <td className="ig-col-concepto ig-concepto-cell">{row.concepto}</td>
                        {row.meses.map((cell, i) => {
                          const mes = i + 1;
                          const isMulti = row.multiMeses[i];
                          return (
                            <td
                              key={i}
                              className={`ig-cell${cell ? ' ig-cell-has-value' : ''}${isMulti ? ' ig-cell-multi' : ''}`}
                              onClick={() => handleCellClick(cat, row.concepto, mes, cell, isMulti)}
                            >
                              {cell ? (
                                <span className={cell.cantidad < 0 ? 'negative' : ''}>
                                  {formatCell(Number(cell.cantidad))}
                                </span>
                              ) : null}
                            </td>
                          );
                        })}
                        <td className="ig-total">
                          <span className={row.total < 0 ? 'negative' : ''}>
                            {formatCell(row.total)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              {tableData.size === 0 && (
                <tr>
                  <td colSpan={14} className="ig-no-data">
                    No hay registros para este inmueble y año. Usa "+ Añadir entrada" para comenzar.
                  </td>
                </tr>
              )}
              {tableData.size > 0 && (
                <>
                  <tr className="ig-grand-total-row">
                    <td className="ig-col-concepto ig-concepto-cell">TOTAL</td>
                    {monthTotals.map((v, i) => (
                      <td key={i} className="ig-grand-total-cell">
                        {v !== 0 ? (
                          <span className={v < 0 ? 'negative' : ''}>{formatCell(v)}</span>
                        ) : null}
                      </td>
                    ))}
                    <td className="ig-grand-total-cell ig-total">
                      <span className={grandTotal < 0 ? 'negative' : ''}>{formatCell(grandTotal)}</span>
                    </td>
                  </tr>
                  <tr className="ig-spacer-row"><td colSpan={14}></td></tr>
                  <tr className="ig-derived-row ig-resultado-row">
                    <td className="ig-col-concepto ig-concepto-cell">RESULTADO</td>
                    {resultadoMonths.map((v, i) => (
                      <td key={i} className="ig-derived-cell">
                        {v !== 0 ? <span className={v < 0 ? 'negative' : ''}>{formatCell(v)}</span> : null}
                      </td>
                    ))}
                    <td className="ig-derived-cell ig-derived-cell-total">
                      <span className={resultado < 0 ? 'negative' : ''}>{formatCell(resultado)}</span>
                    </td>
                  </tr>
                  <tr className="ig-derived-row ig-reserva-row">
                    <td className="ig-col-concepto ig-concepto-cell">RESERVA (10%)</td>
                    {reservaMonths.map((v, i) => (
                      <td key={i} className="ig-derived-cell">
                        {v !== 0 ? <span className={v < 0 ? 'negative' : ''}>{formatCell(v)}</span> : null}
                      </td>
                    ))}
                    <td className="ig-derived-cell ig-derived-cell-total">
                      <span className={reservaTotal < 0 ? 'negative' : ''}>{formatCell(reservaTotal)}</span>
                    </td>
                  </tr>
                  <tr className="ig-derived-row ig-reinversion-row">
                    <td className="ig-col-concepto ig-concepto-cell">REINVERSIÓN (20%)</td>
                    {reinversionMonths.map((v, i) => (
                      <td key={i} className="ig-derived-cell">
                        {v !== 0 ? <span className={v < 0 ? 'negative' : ''}>{formatCell(v)}</span> : null}
                      </td>
                    ))}
                    <td className="ig-derived-cell ig-derived-cell-total">
                      <span className={reinversionTotal < 0 ? 'negative' : ''}>{formatCell(reinversionTotal)}</span>
                    </td>
                  </tr>
                  <tr className="ig-derived-row ig-inversores-row">
                    <td className="ig-col-concepto ig-concepto-cell">INVERSORES (70%)</td>
                    {inversoresMonths.map((v, i) => (
                      <td key={i} className="ig-derived-cell">
                        {v !== 0 ? <span className={v < 0 ? 'negative' : ''}>{formatCell(v)}</span> : null}
                      </td>
                    ))}
                    <td className="ig-derived-cell ig-derived-cell-total">
                      <span className={inversoresTotal < 0 ? 'negative' : ''}>{formatCell(inversoresTotal)}</span>
                    </td>
                  </tr>
                  <tr className="ig-spacer-row"><td colSpan={14}></td></tr>
                  <tr className="ig-derived-row ig-neta-row">
                    <td className="ig-col-concepto ig-concepto-cell">RESERVA - GASTOS</td>
                    {reservaNetaMonths.map((v, i) => (
                      <td key={i} className="ig-derived-cell">
                        {v !== 0 ? <span className={v < 0 ? 'negative' : ''}>{formatCell(v)}</span> : null}
                      </td>
                    ))}
                    <td className="ig-derived-cell ig-derived-cell-total">
                      <span className={reservaNetaTotal < 0 ? 'negative' : ''}>{formatCell(reservaNetaTotal)}</span>
                    </td>
                  </tr>
                  <tr className="ig-derived-row ig-neta-row">
                    <td className="ig-col-concepto ig-concepto-cell">REINVERSIÓN - GASTOS</td>
                    {reinversionNetaMonths.map((v, i) => (
                      <td key={i} className="ig-derived-cell">
                        {v !== 0 ? <span className={v < 0 ? 'negative' : ''}>{formatCell(v)}</span> : null}
                      </td>
                    ))}
                    <td className="ig-derived-cell ig-derived-cell-total">
                      <span className={reinversionNetaTotal < 0 ? 'negative' : ''}>{formatCell(reinversionNetaTotal)}</span>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="ig-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="ig-modal">
            <div className="ig-modal-header">
              <h3>{editingRecord ? 'Editar entrada' : 'Añadir entrada'}</h3>
              <button className="ig-modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleModalSubmit} className="ig-modal-form">
              <div className="ig-form-group ig-autocomplete-group" ref={conceptoRef}>
                <label>Concepto</label>
                <input
                  type="text"
                  required
                  value={form.concepto}
                  onChange={(e) => {
                    const val = e.target.value;
                    setForm((f) => ({ ...f, concepto: val }));
                    if (val.trim()) {
                      const lower = val.toLowerCase();
                      setConceptoSuggestions(
                        knownConcepts.filter((c) => c.toLowerCase().includes(lower))
                      );
                    } else {
                      setConceptoSuggestions([]);
                    }
                  }}
                  onBlur={() => setTimeout(() => setConceptoSuggestions([]), 150)}
                  placeholder="Ej: Alquiler, Comunidad..."
                  className="ig-input"
                  autoComplete="off"
                />
                {conceptoSuggestions.length > 0 && (
                  <ul className="ig-autocomplete-list">
                    {conceptoSuggestions.map((c) => (
                      <li
                        key={c}
                        className="ig-autocomplete-item"
                        onMouseDown={() => {
                          setForm((f) => ({ ...f, concepto: c }));
                          setConceptoSuggestions([]);
                        }}
                      >
                        {c}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="ig-form-group">
                <label>Categoría</label>
                <select
                  value={form.categoria}
                  onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))}
                  className="ig-input"
                >
                  <option value="Ingresos">Ingresos</option>
                  <option value="Gastos ordinarios">Gastos ordinarios</option>
                  <option value="Gastos directos">Gastos directos</option>
                  <option value="Gastos reserva">Gastos reserva</option>
                  <option value="Gastos reinversión">Gastos reinversión</option>
                </select>
              </div>

              <div className="ig-form-row ig-form-row-3">
                <div className="ig-form-group">
                  <label>Día</label>
                  <input
                    type="number"
                    min={1}
                    max={31}
                    value={form.dia}
                    onChange={(e) => setForm((f) => ({ ...f, dia: Number(e.target.value) }))}
                    className="ig-input"
                  />
                </div>
                <div className="ig-form-group">
                  <label>Mes</label>
                  <select
                    value={form.mes}
                    onChange={(e) => setForm((f) => ({ ...f, mes: Number(e.target.value) }))}
                    className="ig-input"
                  >
                    {MESES.map((m, i) => (
                      <option key={i} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div className="ig-form-group">
                  <label>Año</label>
                  <input
                    type="number"
                    value={form.anio}
                    onChange={(e) => setForm((f) => ({ ...f, anio: Number(e.target.value) }))}
                    className="ig-input"
                  />
                </div>
              </div>

              <div className="ig-form-group">
                <label>
                  Cantidad{' '}
                  <span className="ig-hint">
                    {(CATEGORIAS_INGRESO as readonly string[]).includes(form.categoria)
                      ? '(positiva)'
                      : '(negativa, ej: -120,50)'}
                  </span>
                </label>
                <input
                  type="text"
                  required
                  value={form.cantidad}
                  onChange={(e) => setForm((f) => ({ ...f, cantidad: e.target.value }))}
                  placeholder={
                    (CATEGORIAS_INGRESO as readonly string[]).includes(form.categoria)
                      ? '850,00'
                      : '-120,50'
                  }
                  className="ig-input"
                />
              </div>

              {formError && <div className="ig-form-error">{formError}</div>}

              <div className="ig-modal-actions">
                {editingRecord && (
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={handleDelete}
                    disabled={deleting || saving}
                  >
                    {deleting ? 'Eliminando...' : 'Eliminar'}
                  </button>
                )}
                <div className="ig-modal-actions-right">
                  <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={saving || deleting}>
                    {saving ? 'Guardando...' : 'Guardar'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default IngresosGastosPage;
